import { useState } from 'react';
import { Center, Group, Loader, SimpleGrid, Stack, Switch } from '@mantine/core';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/auth/useAuth';
import { useOrg } from '@/auth/OrgContext';
import { errorMessage } from '@/api/errors';
import { canCreateProject, useProjects } from '@/api/projects';
import { EmptyState } from '@/components/EmptyState';
import { StatCard } from '@/components/StatCard';
import { usePageTitle } from '@/components/usePageTitle';
import { CreateProjectModal } from '@/features/projects/CreateProjectModal';
import { ProjectList } from '@/features/projects/ProjectList';
import { ClientShell } from '@/shells/ClientShell';

export const Route = createFileRoute('/_client/')({ component: ProjectsPage });

function ProjectsPage() {
  const { t } = useTranslation('projects');
  const { t: tc } = useTranslation();
  usePageTitle(tc('projects.title'));
  const { me } = useAuth();
  const { current, currentRole } = useOrg();
  const [archived, setArchived] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const { data, isPending, isError, error } = useProjects({ organization: current?.public_id, includeArchived: archived });
  const mayCreate = canCreateProject(currentRole, me?.is_staff) && !!current;
  const active = data?.filter((p) => p.state === 'active').length ?? 0;

  return (
    <ClientShell
      eyebrow={current?.name}
      title={tc('projects.title')}
      cta={mayCreate ? { label: t('list.newProject'), onClick: () => setCreateOpen(true) } : undefined}
    >
      <Stack gap="lg">
        <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
          <StatCard label={t('list.stats.projects')} value={data?.length ?? '—'} />
          <StatCard label={t('list.stats.active')} value={data ? active : '—'} accent />
          <StatCard label={t('list.stats.resultsReady')} value={data ? data.reduce((n, p) => n + (p.analyses_summary?.delivered ?? 0), 0) : '—'} />
        </SimpleGrid>
        <Group justify="flex-end">
          <Switch label={t('list.showArchived')} checked={archived} onChange={(e) => setArchived(e.currentTarget.checked)} />
        </Group>
        {isPending ? (
          <Center py="xl">
            <Loader color="forest" />
          </Center>
        ) : isError ? (
          <EmptyState title={errorMessage(error)} />
        ) : (
          <ProjectList projects={data} onCreate={mayCreate ? () => setCreateOpen(true) : undefined} />
        )}
      </Stack>
      {current && (
        <CreateProjectModal
          opened={createOpen}
          onClose={() => setCreateOpen(false)}
          organizations={[current]}
          showInternal={me?.is_staff}
        />
      )}
    </ClientShell>
  );
}
